
import { useState, useMemo } from 'react';
import { T, amountStyle } from '../theme';
import { FinanceViewTopBar, ReportBar } from './NavShared';
import { Camera, Plus, TrendingUp, Receipt, BarChart2, AlertCircle, House, Eye, Briefcase,  Banknote, HandCoins, Landmark, Sparkles, ChevronDown,
   Upload, CreditCard, X, SquarePlus, Info, CheckCircle2, Link2 } from 'lucide-react';
import GettingStartedCard from './GettingStartedCard';
import { getPresetRange } from "../components/DateRangePicker";
import { computeAccountBalance } from '../utils/accountBalance';
import { computeReimbursableExpenses } from '../utils/reimbursableExpenses';


const ICON_MAP = {
  Receipt:          <Receipt size={20} color="#0A7C8A" />,
  ReceiptAmber:     <Receipt size={20} color="#b45309" />,
  Briefcase:        <Briefcase size={20} color="#15803d" />,
  BriefcaseAmber:   <Briefcase size={20} color="#b45309" />,
  HandCoins:        <HandCoins size={20} color="#0891b2" />,
  PersonalFinance:  <House size={20} color="#15803d" />,
  PersonalExpense:  <House size={20} color="#b45309" />,
  AlertCircle:      <AlertCircle size={20} color="#92400e" />,
  BarChart2:        <BarChart2 size={20} color="#0A7C8A" />,
  BarChart2Navy:    <BarChart2 size={20} color="#1e3a5f" />,
  TrendingUp:       <TrendingUp size={20} color="#1e3a5f" />,
  Landmark:         <Landmark size={20} color="#15803d" />,
  Banknote:         <Banknote size={20} color="#15803d" />,
  AddAccount:       <SquarePlus size={20} color="#15803d" />,
  Import:           <Upload size={20} color="#0891b2" />,
  HelpCenter:       <Info size={20} color="#5f8dc3" />,
};


function GridCard({ icon, label, sublabel, onClick, accentBg = '#EFF6FF', stats }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'flex-start', gap: 10,
        background: '#fff', border: `1.5px solid ${accentBg}`,
        borderRadius: 14, padding: '14px 14px',
        cursor: 'pointer', textAlign: 'left',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: 10, background: accentBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111', lineHeight: 1.3 }}>{label.replace('{{STATS}}', stats ?? '')}</div>
        {sublabel && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3, lineHeight: 1.3 }}>{sublabel}</div>}
      </div>
    </button>
  );
}

function resolveViewHandler(view, { onGoToExpenses, onGoToFinancial, onGoToTax, onGoToFinances, onGoToBSVFiltered, onCreateAccount, onImportTransactions }) {
  switch (view.target) {
    case 'expenses':           return onGoToExpenses;
    case 'financial':          return onGoToFinancial;
    case 'tax':                return onGoToTax;
    case 'finances':           return onGoToFinances;
    case 'createAccount':      return onCreateAccount;
    case 'importTransactions': return onImportTransactions;
    case 'helpcenter':         return () => { window.location.href = '/help'; };
    case 'bsv':
    default:
      return () => onGoToBSVFiltered({
        ...(view.filterSet ? { filterSet: view.filterSet } : {}),
        ...(view.fileSelection ? { fileSelection: view.fileSelection } : {}),
        groupBy: view.groupBy,
        title: view.title,
        ...(view.dateFrom != null ? { dateFrom: view.dateFrom } : {}),
        ...(view.dateTo   != null ? { dateTo:   view.dateTo   } : {}),
      });
  }
}

const analysisViews = [
  {
    id: 'financial_summary',
    label: 'Financial Summary',
    sublabel: 'Income vs expenses breakdown',
    icon: 'TrendingUp',
    accentBg: '#EFF6FF',
    target: 'financial',
    enabled: true,
    order: 0,
  },
  {
    id: 'tax_summary',
    label: 'Tax Summary',
    sublabel: 'IRS deduction categories',
    icon: 'Receipt',
    accentBg: '#FAF5FF',
    target: 'tax',
    enabled: true,
    order: 1,
  },
    {
      id: 'business_pl',
      label: 'Business P&L',
      sublabel: 'Profit & Loss for business',
      icon: 'Briefcase',
      accentBg: '#c6d7f6',
      target: 'bsv',
      filterSet: ['business_income', 'business_expense', 'business_expense_refund'],
      groupBy: 'classification',
      dateFrom: getPresetRange('ytd').from,
      dateTo: getPresetRange('ytd').to,
      title: 'Business P&L',
      enabled: true,
      order: 2,
    },
  {
    id: 'needs_review',
    label: '{{STATS}} transactions Need Review',
    sublabel: 'Unclassified transactions',
    icon: 'AlertCircle',
    accentBg: '#FFFBEB',
    target: 'bsv',
    filterSet: ['unclassified_debit', 'unclassified_credit'],
    groupBy: 'category',
    dateFrom: '',
    dateTo: '',
    title: 'Needs Review',
    enabled: true,
    statsKey: 'unreviewed',
    order: 3,
  },
    {
    id: 'helpcenter',
    label: 'Help Center',
    sublabel: 'Access guides and other resources to help you get the most out of MySpend.info',
    icon: 'HelpCenter',
    accentBg: '#e8ebed',
    target: 'helpcenter',
    dateFrom: '',
    dateTo: '',
    title: 'Help Center',
    enabled: true,
    order: 0,
  },
  // {
  //   id: 'add_account',
  //   label: 'Add Account',
  //   sublabel: 'Bank or credit card',
  //   icon: 'AddAccount',
  //   accentBg: '#ECFDF5',
  //   target: 'createAccount',
  //   enabled: true,
  //   order: 3,
  // },
  // {
  //   id: 'import_transactions',
  //   label: 'Import Transactions',
  //   sublabel: 'Upload a bank or CC statement',
  //   icon: 'Import',
  //   accentBg: '#E0F2FE',
  //   target: 'importTransactions',
  //   enabled: true,
  //   order: 4,
  // },
];

function StatPill({ value, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 10, padding: '8px 6px', cursor: onClick ? 'pointer' : 'default',
        minWidth: 0,
      }}
    >
      <span style={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>{value}</span>
      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 2, textAlign: 'center', lineHeight: 1.2 }}>{label}</span>
    </button>
  );
}

export default function DashboardPage({
  user,
  topBarProps,
  onGoToAdd,
  onGoToCapture,
  onGoToFinancial,
  onGoToTax,
  onGoToExpenses,
  onGoToFinances,
  onGoToBSVFiltered,
  onNewReport,
  presetViews,
  stats,
  onBookkeeperClick,
  showGettingStarted,
  onDismissGettingStarted,
  hasExpenses,
  hasImportedTransactions,
  hasChatted,
  onImportCreditCard,
  onImportBank,
  onImportBrokerage,
  onCreateAccount,
  onGoToTransactionAccount,
  transactionFiles = [],
  expenseFiles = [],
  transactionAccounts = [],
  onReimbursementClick,
  reportName,
  autoSaving,
  currentFileAccess,
  onShareClick,
  onLoadClick,
  onCreateReviewLinkClick,
  onReconcileClick,
}) {
  const [extraExpanded, setExtraExpanded] = useState(false);
  const [accountsExpanded, setAccountsExpanded] = useState(true);
  const [showImportChooser, setShowImportChooser] = useState(false);
  const displayName = user?.displayName || user?.email?.split('@')[0] || 'there';
  const onImportTransactions = (onImportCreditCard || onImportBank || onImportBrokerage) ? () => setShowImportChooser(true) : undefined;

  // Compute balances from already-loaded transaction data
  const { balances: accountBalances, lastTxDateByAccount } = useMemo(() => {
    const metaByName = {};
    for (const acc of transactionAccounts) metaByName[acc.name || acc.id] = acc;

    const byAccount = {};
    for (const f of transactionFiles) {
      if (f.isLocal) continue;
      const accountName = f.accountName || f.name;
      if (!byAccount[accountName]) byAccount[accountName] = { reportType: f.reportType ?? 'credit_card', txs: [] };
      if (Array.isArray(f.data)) byAccount[accountName].txs.push(...f.data);
      else if (typeof f.data === 'string') {
        try { byAccount[accountName].txs.push(...JSON.parse(f.data)); } catch {}
      }
    }

    const result = {};
    const lastTxDateByAccount = {};
    for (const [accountName, { reportType, txs }] of Object.entries(byAccount)) {
      const meta = metaByName[accountName] ?? {};
      result[accountName] = computeAccountBalance({
        transactions: txs,
        openingBalance: meta.openingBalance,
        openingBalanceDate: meta.openingBalanceDate,
        reportType,
        asOfDate: null,
      });
      const dates = txs.map(t => t.date).filter(Boolean);
      if (dates.length) lastTxDateByAccount[accountName] = dates.reduce((a, b) => a > b ? a : b);
    }
    return { balances: result, lastTxDateByAccount };
  }, [transactionFiles, transactionAccounts]);

  const reimbursableResult = useMemo(() => {
    const businessAccounts = transactionAccounts.filter(a => a.type === 'business').map(a => a.name || a.id);
    return computeReimbursableExpenses({ transactionFiles, expenseFiles, excludeAccounts: businessAccounts });
  }, [transactionFiles, expenseFiles, transactionAccounts]);

  const totalReimbursed = reimbursableResult.reimbursedTotal;

  const pendingOwnerPayments = useMemo(() => {
    const payments = [];
    for (const f of transactionFiles) {
      if (f.isLocal) continue;
      let txs = [];
      if (Array.isArray(f.data)) txs = f.data;
      else if (typeof f.data === 'string') { try { txs = JSON.parse(f.data); } catch {} }
      for (const tx of txs) {
        if (tx.businessClassification === 'owner_reimbursement' && !tx.reimbursed) {
          payments.push({ ...tx, filename: f.name, source_type: 'transaction' });
        }
      }
    }
    return payments;
  }, [transactionFiles]);

  const navHandlers = { onGoToExpenses, onGoToFinancial, onGoToTax, onGoToFinances, onGoToBSVFiltered, onCreateAccount, onImportTransactions };
  const primaryViews = (presetViews ?? []).slice(0, 4);
  const extraViews   = (presetViews ?? []).slice(4);

  return (
    <>
      {showImportChooser && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: '24px 20px',
            width: '100%', maxWidth: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <span style={{ fontSize: 17, fontWeight: 800, color: '#111' }}>Import Transactions</span>
              <button onClick={() => setShowImportChooser(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={20} color="#6B7280" />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {onImportBank && (
                <button
                  onClick={() => { setShowImportChooser(false); onImportBank(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: '#F0FDF4', border: '1.5px solid #BBF7D0',
                    borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Landmark size={20} color="#15803d" />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>Bank Statement</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Checking or savings account</div>
                  </div>
                </button>
              )}
              {onImportCreditCard && (
                <button
                  onClick={() => { setShowImportChooser(false); onImportCreditCard(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: '#EFF6FF', border: '1.5px solid #BFDBFE',
                    borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CreditCard size={20} color="#1d4ed8" />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>Credit Card Statement</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Visa, Mastercard, Amex, etc.</div>
                  </div>
                </button>
              )}
              {onImportBrokerage && (
                <button
                  onClick={() => { setShowImportChooser(false); onImportBrokerage(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: '#F5F3FF', border: '1.5px solid #DDD6FE',
                    borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <TrendingUp size={20} color="#6d28d9" />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>Brokerage Statement</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>ETrade, Schwab, Fidelity, etc.</div>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div style={{ fontFamily: T.fontBody, background: T.appBg, minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ background: T.gradNavy, padding: '14px 16px 16px' }}>
        {topBarProps && <FinanceViewTopBar {...topBarProps} />}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, color: '#fff', fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>
          {/* <LayoutDashboard size={22} color={T.tealBright} /> */}
          Hi, {displayName}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 2 }}>
          What would you like to do?
        </div>
      </div>

      <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Getting Started */}
        {showGettingStarted && (
          <GettingStartedCard
            hasExpenses={hasExpenses}
            hasImportedTransactions={hasImportedTransactions}
            hasChatted={hasChatted}
            onAddExpense={onGoToAdd}
            onScanReceipt={onGoToCapture}
            onImportCreditCard={onImportCreditCard}
            onImportBank={onImportBank}
            onBookkeeperClick={onBookkeeperClick}
            onDismiss={onDismissGettingStarted}
          />
        )}

        {/* Quick Actions */}
        <div>
          {/* <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
            Quick Actions
          </div> */}
          <button
            onClick={onBookkeeperClick}
            style={{
              width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              background: '#fff', border: `1.5px solid ${T.tealPale}`,
              borderRadius: 14, padding: '12px 16px', cursor: 'pointer',
              boxShadow: '0 3px 8px rgba(0,0,0,0.05)',
            }}
          >
            <Sparkles size={22} color={T.tealDeep} />
            <span style={{ fontSize: 13, fontWeight: 700, color: T.tealDeep }}>Chat with your AI Bookkeeper</span>
          </button>
        </div>

        {/* Finances */}
        <div>
          {/* <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
            Finances
          </div> */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 158px)', gap: 10, justifyContent: 'center' }}>
            {analysisViews.map(view => (
              <GridCard
                key={view.id}
                icon={ICON_MAP[view.icon] ?? ICON_MAP.BarChart2}
                label={view.label}
                sublabel={view.sublabel}
                accentBg={view.accentBg ?? '#EFF6FF'}
                onClick={resolveViewHandler(view, navHandlers)}
                stats={view.statsKey ? stats[view.statsKey] : undefined || null}
              />
            ))}
            {onReconcileClick && transactionFiles.length > 0 && expenseFiles.length > 0 && (
              <GridCard
                icon={<Link2 size={20} color="#0891b2" />}
                label="Reconcile Expenses to Transactions"
                sublabel="Match expense items to bank or CC entries"
                accentBg="#ECFEFF"
                onClick={onReconcileClick}
              />
            )}
          </div>

        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase' }}>
            <span onClick={onGoToExpenses} style={{ cursor: 'pointer' }}>Active Expense Report</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {hasExpenses && (
                <button onClick={onGoToExpenses} title="View report"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px', borderRadius: 6, color: '#9CA3AF', display: 'flex', alignItems: 'flex-start' }}>
                  <Eye size={15} />
                </button>
              )}
                {/* {onLoadClick && (
                <button onClick={onLoadClick} title="Load report"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px', borderRadius: 6, color: '#9CA3AF', display: 'flex', alignItems: 'center' }}>
                  <FolderOpen size={15} />
                </button>
              )} */}
              {onNewReport && (
                <button onClick={onNewReport} title="New report"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px', borderRadius: 6, color: '#9CA3AF', display: 'flex', alignItems: 'center' }}>
                  <SquarePlus size={15} />
                </button>
              )}
            </div>
          </div>
          <div onClick={onGoToExpenses} style={{ background: T.gradNavy, borderRadius: 14, padding: '5px 5px', cursor: 'pointer' }}>
            <ReportBar
              currentFileName={reportName}
              autoSaving={autoSaving}
              currentFileAccess={currentFileAccess}
              onShareClick={onShareClick}
              onLoadClick={onLoadClick}
              onCreateReviewLinkClick={onCreateReviewLinkClick}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onGoToCapture}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: T.gradTeal, border: 'none', borderRadius: 12, padding: '12px 10px', cursor: 'pointer' }}
            >
              <Camera size={18} color="#fff" />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Scan Receipt</span>
            </button>
            <button
              onClick={onGoToAdd}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: T.navy, border: 'none', borderRadius: 12, padding: '12px 10px', cursor: 'pointer' }}
            >
              <Plus size={18} color="#fff" />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Add Expense</span>
            </button>
          </div>
        </div>

        {/* My Accounts */}
        {onGoToTransactionAccount && transactionFiles.length > 0 && (() => {
          // Group by accountName, pick most recent file per account
          const accountMap = new Map();
          for (const f of transactionFiles) {
            const key = f.accountName || f.name;
            const existing = accountMap.get(key);
            if (!existing || (f.updatedAt ?? 0) > (existing.updatedAt ?? 0)) {
              accountMap.set(key, f);
            }
          }
          const allAccounts = Array.from(accountMap.values());
          const metaByName = Object.fromEntries(transactionAccounts.map(a => [a.name || a.id, a]));
          const personalAccounts = allAccounts.filter(f => (metaByName[f.accountName || f.name]?.type ?? f.type ?? 'personal') !== 'business');
          const businessAccounts = allAccounts.filter(f => (metaByName[f.accountName || f.name]?.type ?? f.type ?? 'personal') === 'business');

          const fmtLastTxDate = (iso) => {
            if (!iso) return null;
            const d = new Date(iso + 'T00:00:00');
            return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
          };

          const renderAccountCard = (f, isBusiness) => {
            const accountName = f.accountName || f.name;
            const bal = accountBalances[accountName];
            const isCreditCard = f.reportType === 'credit_card';
            const isBrokerage  = f.reportType === 'brokerage';
            const balance      = bal?.computed_balance;
            const isNeg        = typeof balance === 'number' && balance < 0;
            const isEstimated  = bal && !bal.has_opening_balance;

            let icon, accentBg, sublabelType;
            if (isBusiness) {
              icon        = isCreditCard ? <CreditCard size={20} color="#b45309" />
                          : isBrokerage  ? <TrendingUp size={20} color="#b45309" />
                          :                <Landmark   size={20} color="#b45309" />;
              accentBg    = '#FEF3C7';
              sublabelType = isCreditCard ? 'Credit card' : isBrokerage ? 'Brokerage' : 'Bank account';
            } else {
              icon        = isCreditCard ? <CreditCard size={20} color="#1d4ed8" />
                          : isBrokerage  ? <TrendingUp size={20} color="#7c3aed" />
                          :                <Landmark   size={20} color="#15803d" />;
              accentBg    = isCreditCard ? '#EFF6FF' : isBrokerage ? '#F5F3FF' : '#ECFDF5';
              sublabelType = isCreditCard ? 'Credit card' : isBrokerage ? 'Brokerage' : 'Bank account';
            }

            return (
              <button
                key={accountName}
                onClick={() => onGoToTransactionAccount(f.name)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8,
                  background: isBusiness ? '#FFFBEB' : '#fff',
                  border: `1.5px solid ${accentBg}`,
                  borderRadius: 14, padding: '14px 14px',
                  cursor: 'pointer', textAlign: 'left',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}
              >
                <div style={{
                  width: 38, height: 38, borderRadius: 10, background: accentBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {icon}
                </div>
                <div style={{ width: '100%' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111', lineHeight: 1.3 }}>{accountName}</div>
                  {typeof balance === 'number' ? (
                    <>
                      <div style={{
                        fontSize: 15, fontWeight: 800, lineHeight: 1.3, marginTop: 4,
                        color: isNeg ? '#dc2626' : '#111',
                        ...amountStyle(),
                      }}>
                        {`${balance < 0 ? '−' : ''}$${Math.abs(balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </div>
                      <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2, lineHeight: 1.2 }}>
                        {isCreditCard ? 'Amount owed' : sublabelType}{isEstimated ? ' · est.' : ''}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3, lineHeight: 1.3 }}>{sublabelType}</div>
                  )}
                  {(() => {
                    const iso = lastTxDateByAccount[accountName];
                    const lastDate = fmtLastTxDate(iso);
                    if (!lastDate) return null;
                    const ageDays = iso ? Math.floor((Date.now() - new Date(iso + 'T00:00:00')) / 86400000) : 0;
                    const dateColor = ageDays > 30 ? '#dc2626' : ageDays > 15 ? '#d97706' : '#9CA3AF';
                    return (
                      <div style={{ fontSize: 10, color: dateColor, marginTop: 1, lineHeight: 1.2 }}>
                        Last txn {lastDate}
                      </div>
                    );
                  })()}
                </div>
              </button>
            );
          };

          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: accountsExpanded ? 10 : 0 }}>
                <button onClick={() => setAccountsExpanded(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9CA3AF', fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>
                  <ChevronDown size={13} style={{ transition: 'transform 0.2s', transform: accountsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                  My Accounts
                </button>
                <div style={{ display: 'flex', gap: 2 }}>
                <button onClick={() => resolveViewHandler({ id: 'import_transactions', target: 'importTransactions' }, navHandlers)()} title="Import Transactions file"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px', borderRadius: 6, color: '#9CA3AF', display: 'flex', alignItems: 'center' }}>
                  <Upload size={15} />
                </button>
                <button onClick={() => resolveViewHandler({ id: 'add_account', target: 'createAccount' }, navHandlers)()} title="Add account"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px', borderRadius: 6, color: '#9CA3AF', display: 'flex', alignItems: 'center' }}>
                  <SquarePlus size={15} />
                </button>
                </div>
              </div>
              {accountsExpanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {personalAccounts.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 158px)', gap: 10, justifyContent: 'center' }}>
                      {personalAccounts.map(f => renderAccountCard(f, false))}
                    </div>
                  )}
                  {businessAccounts.length > 0 && (
                    <div>
                      {personalAccounts.length > 0 && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>Business</div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 158px)', gap: 10, justifyContent: 'center' }}>
                        {businessAccounts.map(f => renderAccountCard(f, true))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Reimbursement card — shown when there are business expenses or unmatched owner payments */}
        {(reimbursableResult.grandTotal > 0 || pendingOwnerPayments.length > 0) && onReimbursementClick && (() => {
          const outstanding = parseFloat((reimbursableResult.grandTotal - totalReimbursed).toFixed(2));
          const settled     = outstanding <= 0 && totalReimbursed > 0 && pendingOwnerPayments.length === 0;
          const fmt = (n) => `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const hasPending  = pendingOwnerPayments.length > 0;
          return (
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
                Reimbursements
              </div>
              <button onClick={() => onReimbursementClick(reimbursableResult, pendingOwnerPayments)} style={{
                width: '100%', display: 'flex', flexDirection: 'column', gap: 10,
                background: settled ? '#F0FDF4' : '#FFFBEB',
                border: `1.5px solid ${settled ? '#bbf7d0' : '#FEF3C7'}`,
                borderRadius: 14, padding: '14px 16px',
                cursor: 'pointer', textAlign: 'left',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: settled ? '#dcfce7' : '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {settled ? <CheckCircle2 size={20} color="#16a34a" /> : <HandCoins size={20} color="#b45309" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>
                      {settled ? 'All settled' : reimbursableResult.grandTotal > 0 ? `Outstanding: ${fmt(outstanding)}` : 'Business payment received'}
                    </div>
                    {reimbursableResult.grandTotal > 0 && (
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                        {fmt(reimbursableResult.grandTotal)} owed · {fmt(totalReimbursed)} received
                      </div>
                    )}
                  </div>
                </div>
                {hasPending && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#FEF3C7', borderRadius: 8, padding: '6px 10px' }}>
                    <span style={{ fontSize: 11, color: '#b45309', fontWeight: 700 }}>
                      {pendingOwnerPayments.length === 1
                        ? `Business paid ${fmt(parseFloat((pendingOwnerPayments[0].convertedTotal ?? pendingOwnerPayments[0].total ?? pendingOwnerPayments[0].amount ?? 0)))} — mark personal expenses as reimbursed?`
                        : `${pendingOwnerPayments.length} business payment(s) not yet matched to expenses`}
                    </span>
                  </div>
                )}
              </button>
            </div>
          );
        })()}

        {/* Preset Views — primary */}
        {primaryViews.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
              Preset Views
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 158px)', gap: 10, justifyContent: 'center' }}>
              {primaryViews.map(view => (
                <GridCard
                  key={view.id}
                  icon={ICON_MAP[view.icon] ?? ICON_MAP.BarChart2}
                  label={view.label}
                  sublabel={view.sublabel}
                  accentBg={view.accentBg ?? '#EFF6FF'}
                  onClick={resolveViewHandler(view, navHandlers)}
                  stats={view.statsKey ? stats[view.statsKey] : undefined || null}
                />
              ))}
            </div>

            {/* Additional Views — collapsed by default */}
            {extraViews.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <button
                  onClick={() => setExtraExpanded(v => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '4px 0', color: '#6B7280', fontSize: 12, fontWeight: 700,
                  }}
                >
                  <ChevronDown
                    size={15}
                    style={{ transition: 'transform 0.2s', transform: extraExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                  />
                  Additional Views
                </button>
                {extraExpanded && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 158px)', gap: 10, justifyContent: 'center', marginTop: 10 }}>
                    {extraViews.map(view => (
                      <GridCard
                        key={view.id}
                        icon={ICON_MAP[view.icon] ?? ICON_MAP.BarChart2}
                        label={view.label}
                        sublabel={view.sublabel}
                        accentBg={view.accentBg ?? '#EFF6FF'}
                        onClick={resolveViewHandler(view, navHandlers)}
                        stats={view.statsKey ? stats[view.statsKey] : undefined || null}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
    <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 36 }}></div>
  </>
  );
}
